import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {getReports} from '../../../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {ExperiencesPage} from '../../../../features/reports/components/ExperiencesPage';
import {withHandler} from '../../../../server/token/withHandler';
import {ScriptForGA} from '../../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  companyName?: string;
};

const QueryParameter = z.object({
  id: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const props: Props = {};

    const {id} = context.validatedQuery;
    // SEOの観点から未ログイン状態でも企業名を表示するために先に取得する
    const result = await getReports({
      queryParams: {
        companyIDs: Number(id),
        types: [],
        departmentMasterIDs: [],
        limit: 10,
      },
    });
    const companyName = result.ok ? result.value.reports[0]?.companyName : '';

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          companyName,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        companyName,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  companyName,
}: Props) => {
  return (
    <>
      <ExperiencesPage companyName={companyName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
